export type ConstantType<T> = { [P in keyof T]: string | number };

/**
 * Annotation which can be used for compile time validation for a singleton class only containing constants.
 *
 * (See example class 'MyHolder')
 *
 * @param target
 * @returns {{CONSTS: T}}
 */
export function constantsHolder<T extends ConstantType<T>>(target: {
	CONSTS: T;
	new (...args): T;
}): any {
	// assert target contains the CONSTS method
	assertPrimitive(target.CONSTS);

	// disable the constructor
	target.constructor = () => {
		throw new Error('Tried to instantiate a singleton');
	};

	return target;

	function assertPrimitive<Q>(value: ConstantType<Q>) {
		// do nothing
	}
}

export type Constant<T extends ConstantType<T>> = T[keyof T];

/**
 * Example of constantsHolder usage and type validation
 */
@constantsHolder
class MyHolder {
	// required
	public static readonly CONSTS = new MyHolder();

	// number example
	public readonly CONSTANT_NUMBER = 10;
	// string example
	public readonly CONSTANT_STRING = 'my string';

	// Possible but breaks type checking (can't fix)
	// public readonly BREAKING_CONSTANT_NUMBER: number;
	// Possible but breaks type checking (can't fix)
	// public readonly BREAKING_CONSTANT_STRING: string;

	// not possible:
	// public readonly CONSTANT_OBJECT = {};

	private demonstateTypeCheck() {
		// valid
		this.testValidConstant(MyHolder.CONSTS.CONSTANT_NUMBER);
		this.testValidConstant(10);
		this.testValidConstant(MyHolder.CONSTS.CONSTANT_STRING);
		this.testValidConstant('my string');

		// invalid
		// this.testValidConstant(11);
		// this.testValidConstant('not my string');
		// this.testValidConstant({});
		// this.testValidConstant(undefined);
	}

	private testValidConstant(constantValue: Constant<MyHolder>) {
		// do nothing
	}
}
